// ── The guide a screen gives about itself ────────────────────────────────────
//
// Every screen in this application is somebody's first screen once. The usual
// answers are a manual nobody opens and a tour that blocks the interface with
// "step 1 of 6" before the person has seen anything.
//
// This is the third answer: the screen explains its own parts, in place, and
// only when asked. A "?" beside the title turns short notes on beside the
// things they describe, and turns them off again. Nothing overlays, nothing
// waits for a click, nothing has to be finished.
//
// Off is the only default. It never opens by itself, not even the first time:
// somebody who knows the screen would have to close it, and an explanation that
// has to be dismissed is an interruption whatever it says. It is there for the
// moment somebody wants it, and invisible for every moment they do not.

import { computed, ref } from 'vue'

/**
 * The guide showing right now, by id, or null.
 *
 * One at a time, deliberately: two screens are never mounted at once, so a note
 * does not have to know which guide it belongs to in order to appear with it.
 */
const showing = ref(null)

/** Whether notes should be on screen. Read by UiGuideNote. */
export const guideShowing = computed(() => showing.value !== null)

/**
 * @param {string} id  stable per screen, e.g. 'quotes.catalog'.
 */
export function useGuide(id) {
  const open = computed(() => showing.value === id)

  const toggle = () => { showing.value = open.value ? null : id }

  /** On leaving the screen, so the next one does not inherit its notes. */
  const close = () => { if (open.value) showing.value = null }

  return { open, toggle, close }
}
